import { useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";

import { queryClient } from "App";
import BaseTableV3 from "components/BaseTableV3/BaseTableV3";
import { getNextQueryParams } from "components/helpers";
import RuvixxDate from "components/RuvixxDate";
import useMetaSyncInProgress, {
  META_SYNC_POLL_INTERVAL_MS,
} from "hooks/useMetaSyncInProgress";
import MetaInsightService from "services/MetaInsight";

const insightsQueryKey = accountId => ["meta-insights", accountId];

const INSIGHT_COLUMNS = [
  {
    header: "ID",
    id: "ID",
    accessorKey: "id",
    size: 50,
    cell: ({ row }) => (
      <p style={{ fontWeight: "bold" }}>{row.getValue("ID")}</p>
    ),
  },
  { id: "Level", accessorKey: "level", header: "Level", size: 110 },
  { id: "Object", accessorKey: "object_name", header: "Object", size: 200 },
  {
    id: "Date",
    accessorKey: "date_start",
    header: "Date",
    size: 150,
    enableSorting: true,
    cell: ({ row }) => {
      const date = row.getValue("Date");
      return date ? <RuvixxDate date={date} /> : null;
    },
  },
  { id: "Spend", accessorKey: "spend", header: "Spend", size: 110 },
  {
    id: "Impressions",
    accessorKey: "impressions",
    header: "Impressions",
    size: 120,
  },
  { id: "Clicks", accessorKey: "clicks", header: "Clicks", size: 100 },
  { id: "CTR", accessorKey: "ctr", header: "CTR", size: 90 },
  { id: "Reach", accessorKey: "reach", header: "Reach", size: 110 },
  {
    id: "Conversions",
    accessorKey: "conversions",
    header: "Conversions",
    size: 120,
  },
];

const MetaInsightsTab = () => {
  const { accountId } = useParams();

  const columns = useMemo(() => INSIGHT_COLUMNS, []);
  const isSyncing = useMetaSyncInProgress(accountId, "live");

  const invalidateInsights = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: insightsQueryKey(accountId),
      }),
    [accountId]
  );

  const fetchInsights = useCallback(
    async queryParams => {
      const next = getNextQueryParams(queryParams, columns);
      return MetaInsightService.list(accountId, next);
    },
    [accountId, columns]
  );

  return (
    <div className="tw:flex tw:min-h-0 tw:min-w-0 tw:flex-1 tw:flex-col">
      <BaseTableV3
        columns={columns}
        header="Insights"
        tableName="meta_insights"
        queryKey={insightsQueryKey(accountId)}
        fetchData={fetchInsights}
        invalidateData={invalidateInsights}
        refetchInterval={isSyncing ? META_SYNC_POLL_INTERVAL_MS : undefined}
        enableSearch
        enableSettings
      />
    </div>
  );
};

export default MetaInsightsTab;
